import React, { useState, useEffect } from "react";
import { BiDownload, BiFilter, BiRefresh, BiUser } from "react-icons/bi";
import { useClientReport } from "../../../hooks";
import {
  formatCurrency,
  formatNumber,
  formatPhoneNumber,
  formatDisplayDate,
} from "../../../utils/formatters";
import {
  ReportFilters,
  ReportTabs,
  KPICard,
  DataTable,
  ExportModal,
} from "./shared";

const ClientsReport = () => {
  const [showFilters, setShowFilters] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showClientDetails, setShowClientDetails] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    isActive: true,
    minAppointments: null,
    hasActiveSubscription: null,
    startDate: null,
    endDate: null,
  });

  const { loading, error, getClients, getClientDetails } = useClientReport();

  const [clients, setClients] = useState({
    data: [],
    pagination: {},
    summary: {},
  });
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    loadClients();
  }, [filters]);

  const loadClients = async (page = 1) => {
    try {
      const result = await getClients({ ...filters, page });
      setClients(result);
    } catch (error) {
      console.error("Erreur chargement clients:", error);
    }
  };

  const handlePageChange = async (page) => {
    await loadClients(page);
  };

  const handleRowClick = async (client) => {
    try {
      const details = await getClientDetails(client.id);
      setSelectedClient(details);
      setShowClientDetails(true);
    } catch (error) {
      console.error("Erreur chargement détails client:", error);
    }
  };

  const handleExport = async (format, options) => {
    // Implémenter l'export
    console.log("Export:", format, options);
  };

  const clientColumns = [
    {
      key: "name",
      label: "Client",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
            <BiUser className="text-secondary-600" />
          </div>
          <div>
            <p className="font-medium">
              {row.prenom} {row.nom}
            </p>
            <p className="text-xs text-gray-500">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "telephone",
      label: "Téléphone",
      render: (row) => formatPhoneNumber(row.telephone),
    },
    {
      key: "totalAppointments",
      label: "Rendez-vous",
      render: (row) => formatNumber(row.totalAppointments),
    },
    {
      key: "totalSpent",
      label: "Total dépensé",
      render: (row) => formatCurrency(row.totalSpent),
    },
    {
      key: "lastAppointment",
      label: "Dernier RDV",
      render: (row) =>
        row.lastAppointment ? formatDisplayDate(row.lastAppointment) : "-",
    },
    {
      key: "hasActiveSubscription",
      label: "Abonnement",
      render: (row) =>
        row.hasActiveSubscription ? (
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
            Actif
          </span>
        ) : (
          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
            Aucun
          </span>
        ),
    },
    {
      key: "isActive",
      label: "Statut",
      render: (row) =>
        row.isActive ? (
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
            Actif
          </span>
        ) : (
          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
            Inactif
          </span>
        ),
    },
  ];

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Une erreur est survenue : {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-poppins font-semibold text-[20px] text-title">
          Rapport clients
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiFilter size={18} />
            Filtres
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
          >
            <BiDownload size={18} />
            Exporter
          </button>
          <button
            onClick={() => loadClients()}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiRefresh size={18} />
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-lg mb-6">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-poppins font-semibold text-[16px]">
              Filtres clients
            </h3>
            <button
              onClick={() => setShowFilters(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <BiFilter size={20} />
            </button>
          </div>
          <div className="p-4 space-y-4">
            {/* Recherche */}
            <div>
              <label className="block font-nunito text-[14px] font-medium text-text mb-2">
                Recherche
              </label>
              <input
                type="text"
                placeholder="Nom, email, téléphone..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            {/* Filtres */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.isActive}
                    onChange={(e) =>
                      setFilters({ ...filters, isActive: e.target.checked })
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="font-nunito text-[14px]">
                    Clients actifs uniquement
                  </span>
                </label>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.hasActiveSubscription}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        hasActiveSubscription: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="font-nunito text-[14px]">
                    Avec abonnement actif
                  </span>
                </label>
              </div>
            </div>

            {/* Nombre minimum de rendez-vous */}
            <div>
              <label className="block font-nunito text-[14px] font-medium text-text mb-2">
                Nombre minimum de rendez-vous
              </label>
              <input
                type="number"
                min="0"
                value={filters.minAppointments || ""}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    minAppointments: e.target.value
                      ? parseInt(e.target.value)
                      : null,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() =>
                  setFilters({
                    search: "",
                    isActive: true,
                    minAppointments: null,
                    hasActiveSubscription: null,
                    startDate: null,
                    endDate: null,
                  })
                }
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Réinitialiser
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 text-sm bg-secondary-500 text-white rounded-lg hover:bg-secondary-600"
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      {clients.summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <KPICard
            title="Total clients"
            value={formatNumber(clients.summary.totalClients)}
            icon="👥"
          />
          <KPICard
            title="Clients actifs"
            value={formatNumber(clients.summary.activeClients)}
            icon="✅"
            color="green"
          />
          <KPICard
            title="Nouveaux clients"
            value={formatNumber(clients.summary.newClientsThisPeriod)}
            icon="🆕"
            color="blue"
          />
          <KPICard
            title="Avec RDV"
            value={formatNumber(clients.summary.clientsWithAppointments)}
            icon="📅"
            color="purple"
          />
          <KPICard
            title="Avec abonnement"
            value={formatNumber(clients.summary.clientsWithSubscriptions)}
            icon="🔄"
            color="orange"
          />
        </div>
      )}

      {/* Tableau des clients */}
      <DataTable
        columns={clientColumns}
        data={clients.data}
        pagination={clients.pagination}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
      />

      {/* Modal détails client */}
      {showClientDetails && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-poppins font-semibold text-[18px]">
                Détails du client
              </h3>
              <button
                onClick={() => setShowClientDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <BiFilter size={20} />
              </button>
            </div>
            <div className="p-6">
              {/* À implémenter : Détails complets du client */}
              <pre>{JSON.stringify(selectedClient, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'export */}
      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
};

export default ClientsReport;
